/**
 * PotentielCard — potentiel d'agrandissement (Phase 3B).
 *
 * Layout :
 *   - Header "Analyse IA · Potentiel d'agrandissement" (même graphisme).
 *   - Hero chiffré : emprise constructible résiduelle en m² (gros) + hauteur
 *     max en niveaux à droite.
 *   - Mini-SVG parcelle/bâti/enveloppe : parcelle (contour sombre), bâti
 *     existant (gris foncé), enveloppe constructible (lime translucide).
 *   - Barres de progression : emprise bâtie vs emprise max PLU.
 *   - Recommandations + caveats (si Claude a répondu).
 *   - Rationale + meta.
 *
 * Quand `unavailableReason` est posé (WFS down, pas de géométrie, CES non
 * exprimé par le PLU), on affiche une carte factuelle à la place du chiffre —
 * pas une bannière d'erreur rouge.
 */

import { useId, useMemo } from "react";
import {
  useAiPotentiel,
  type AiPotentiel,
  type AiPotentielBuilding,
} from "../../lib/ai/potentiel";
import { Card } from "../ui";
import { Ic } from "../icons";

type Props = { ficheId: string };

export function PotentielCard({ ficheId }: Props) {
  const state = useAiPotentiel(ficheId);

  return (
    <Card className="p-6 md:p-7">
      <header className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-ink-50 hairline grid place-items-center text-ink-700">
          <Ic.Ruler size={18} />
        </div>
        <div>
          <div className="text-[11.5px] uppercase tracking-[0.12em] text-lime-700 font-medium">
            Analyse IA
          </div>
          <h2 className="text-[18px] md:text-[19px] font-semibold tracking-tight text-ink-900">
            Potentiel d'agrandissement
          </h2>
        </div>
      </header>

      <div className="mt-5">
        {state.status === "idle" || state.status === "loading" ? (
          <Skeleton />
        ) : state.status === "error" ? (
          <ErrorBox />
        ) : state.data.unavailableReason ? (
          <UnavailableBox data={state.data} />
        ) : (
          <ReadyView data={state.data} />
        )}
      </div>
    </Card>
  );
}

// ─── Ready view ──────────────────────────────────────────────────────────

function ReadyView({ data }: { data: AiPotentiel }) {
  return (
    <div className="space-y-5">
      <Hero data={data} />
      <EmpriseBar data={data} />
      <div className="grid md:grid-cols-[200px,1fr] gap-4 md:gap-5 items-start">
        <ParcelSvg data={data} />
        <KeyFacts data={data} />
      </div>
      {data.recommendations.length > 0 && (
        <RecoList items={data.recommendations} />
      )}
      {data.caveats.length > 0 && <CaveatsList items={data.caveats} />}
      {data.rationale && (
        <p className="text-[13.5px] leading-[1.65] text-ink-700">
          {data.rationale}
        </p>
      )}
      <Meta generatedAt={data.generatedAt} model={data.model} />
    </div>
  );
}

// ─── Hero : emprise constructible + maxLevels ───────────────────────────

function Hero({ data }: { data: AiPotentiel }) {
  const residual = data.residualFootprint_m2;
  const canBuild = residual !== null && residual > 0;
  return (
    <div className="rounded-2xl bg-gradient-to-br from-lime-50 via-paper to-paper hairline px-5 py-5 md:px-6 md:py-6">
      <div className="flex items-start gap-6 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-medium">
            Emprise constructible résiduelle
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-[30px] md:text-[34px] font-semibold tracking-tight text-ink-900 tabular-nums">
              {residual === null
                ? "—"
                : residual <= 0
                  ? "0"
                  : formatInt(residual)}
            </div>
            <div className="text-[14px] text-ink-600">m²</div>
          </div>
          <div className="text-[11.5px] text-ink-500 mt-0.5">
            {canBuild
              ? "au sol, dans la limite du CES et des reculs."
              : residual === 0
                ? "parcelle déjà au maximum autorisé."
                : "CES non exprimé par le règlement."}
          </div>
        </div>
        {data.maxLevels && (
          <div className="ml-auto pl-4 border-l border-ink-200/70 min-w-[96px]">
            <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-medium">
              Hauteur max
            </div>
            <div className="mt-1 text-[22px] font-semibold text-ink-900 tabular-nums">
              {data.maxLevels}
            </div>
            {data.plu.hauteur_max_m !== null && (
              <div className="text-[11.5px] text-ink-500 mt-0.5 tabular-nums">
                {formatInt(data.plu.hauteur_max_m)} m
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Emprise bar (bâti actuel vs PLU max) ───────────────────────────────

function EmpriseBar({ data }: { data: AiPotentiel }) {
  const parcel = data.parcelArea_m2;
  const built = data.builtFootprint_m2;
  const max = data.maxFootprintAllowed_m2;
  if (parcel <= 0) return null;

  const builtPct = Math.min(100, (built / parcel) * 100);
  const maxPct =
    max !== null && max > 0 ? Math.min(100, (max / parcel) * 100) : null;

  return (
    <div className="rounded-xl bg-ink-50/70 hairline px-4 py-3">
      <div className="flex items-center justify-between text-[12px] text-ink-600">
        <span className="flex items-center gap-1.5">
          <Ic.Building size={13} />
          <span>Bâti actuel</span>
        </span>
        <span className="tabular-nums text-ink-900 font-medium">
          {formatInt(built)} m² · {(builtPct).toFixed(1).replace(".", ",")} %
        </span>
      </div>
      <div className="relative mt-2 h-2.5 rounded-full bg-ink-100 overflow-hidden">
        {/* Bâti actuel — gris foncé */}
        <div
          className="absolute inset-y-0 left-0 bg-ink-700 rounded-full"
          style={{ width: `${builtPct}%` }}
        />
        {/* Repère CES PLU — ligne lime */}
        {maxPct !== null && (
          <div
            className="absolute inset-y-0 w-[2px] bg-lime-500"
            style={{ left: `${maxPct}%` }}
            title={`Maximum PLU : ${formatInt(max ?? 0)} m²`}
          />
        )}
      </div>
      <div className="flex items-center justify-between text-[11px] text-ink-500 mt-1.5">
        <span>Parcelle {formatInt(parcel)} m²</span>
        {maxPct !== null ? (
          <span className="tabular-nums">
            Max PLU {formatInt(max ?? 0)} m² ({maxPct.toFixed(1).replace(".", ",")} %)
          </span>
        ) : (
          <span>CES non exprimé</span>
        )}
      </div>
    </div>
  );
}

// ─── Mini SVG parcel + bâti + enveloppe ─────────────────────────────────

function ParcelSvg({ data }: { data: AiPotentiel }) {
  const view = useMemo(() => buildSvgView(data), [data]);
  if (!view) {
    return (
      <div className="rounded-xl bg-ink-50 hairline h-[200px] grid place-items-center text-[11.5px] text-ink-500">
        Parcelle indisponible
      </div>
    );
  }
  const {
    viewBox,
    parcelPath,
    buildingPaths,
    buildablePct,
    parcelSvgArea,
    parcelCentroid,
  } = view;
  // Stable id per render — multiple PotentielCards on the same page would
  // collide otherwise. `useId` gives us a stable cross-render value.
  const clipId = `potentiel-clip-${useId().replace(/:/g, "")}`;
  return (
    <div className="rounded-xl bg-ink-50/60 hairline px-3 py-3">
      <svg
        viewBox={viewBox}
        className="w-full h-auto block"
        aria-label="Parcelle, bâtiments existants et emprise constructible"
      >
        {/* Clip-path used by BuildableHint so the green rectangle never
            spills past the parcel outline, even on highly elongated or
            irregular shapes where a centred square would naturally
            extend beyond the polygon. */}
        <defs>
          <clipPath id={clipId}>
            <path d={parcelPath} />
          </clipPath>
        </defs>
        {/* Parcelle (fond + contour) */}
        <path
          d={parcelPath}
          fill="#ffffff"
          stroke="#0f172a"
          strokeWidth="1.5"
          strokeLinejoin="round"
          fillOpacity="0.9"
        />
        {/* Enveloppe constructible schématique — rectangle lime au centroïde
            de la parcelle, surface proportionnelle au résiduel ET clippé sur
            le contour de la parcelle. Pas une vraie enveloppe respectant les
            reculs (on n'a pas l'axe voirie sans données supplémentaires),
            juste un repère visuel de "combien il reste". */}
        {buildablePct !== null && buildablePct > 0 && parcelSvgArea > 0 && (
          <BuildableHint
            pct={buildablePct}
            parcelSvgArea={parcelSvgArea}
            centroid={parcelCentroid}
            clipId={clipId}
          />
        )}
        {/* Bâtiments existants par-dessus l'enveloppe */}
        {buildingPaths.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="#334155"
            stroke="#0f172a"
            strokeWidth="0.8"
            strokeLinejoin="round"
            fillOpacity="0.95"
          />
        ))}
      </svg>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10.5px] text-ink-600">
        <LegendDot color="#334155" label="Bâti existant" />
        <LegendDot color="#84cc16" label="Potentiel" />
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className="w-2.5 h-2.5 rounded-sm"
        style={{ backgroundColor: color }}
      />
      <span>{label}</span>
    </span>
  );
}

// Small lime rectangle anchored at the parcel centroid, sized to roughly
// match the fraction of the PARCEL (not the viewBox) still constructible,
// AND clipped to the parcel polygon — guarantees zero spillover even on
// highly elongated or irregular parcels. Purely illustrative : the actual
// implantation depends on reculs we don't resolve here.
function BuildableHint({
  pct,
  parcelSvgArea,
  centroid,
  clipId,
}: {
  pct: number; // 0..1 of parcel area
  parcelSvgArea: number; // parcel area in SVG units (shoelace)
  centroid: { x: number; y: number };
  clipId: string;
}) {
  const clamped = Math.min(1, Math.max(0, pct));
  if (clamped <= 0 || parcelSvgArea <= 0) return null;
  // Square sized as a fraction of the PARCEL area. Even when the resulting
  // square would extend past the polygon (e.g. very narrow parcels), the
  // clipPath confines it so the visible green never debords.
  const side = Math.sqrt(clamped * parcelSvgArea);
  return (
    <rect
      x={centroid.x - side / 2}
      y={centroid.y - side / 2}
      width={side}
      height={side}
      rx={Math.min(4, side / 8)}
      fill="#84cc16"
      stroke="#65a30d"
      strokeWidth="1"
      strokeDasharray="3 3"
      fillOpacity="0.28"
      clipPath={`url(#${clipId})`}
    />
  );
}

// ─── Key facts (right column) ───────────────────────────────────────────

function KeyFacts({ data }: { data: AiPotentiel }) {
  const plu = data.plu;
  const cesPct =
    plu.ces_max !== null
      ? plu.ces_max * 100
      : plu.emprise_au_sol_max_pct;
  const rows: Array<{ label: string; value: string }> = [];

  rows.push({
    label: "Parcelle",
    value: `${formatInt(data.parcelArea_m2)} m²`,
  });
  rows.push({
    label: "Bâti existant",
    value: `${formatInt(data.builtFootprint_m2)} m² · ${data.buildings.length} bâtiment${
      data.buildings.length > 1 ? "s" : ""
    }`,
  });
  if (cesPct !== null) {
    rows.push({
      label: "CES max",
      value: `${formatInt(cesPct)} %`,
    });
  }
  if (plu.recul_voie_m !== null) {
    rows.push({
      label: "Recul voie",
      value: `${formatNumber(plu.recul_voie_m)} m`,
    });
  }
  if (plu.recul_limites_m !== null) {
    rows.push({
      label: "Recul limites",
      value: `${formatNumber(plu.recul_limites_m)} m`,
    });
  }
  if (plu.espaces_verts_min_pct !== null) {
    rows.push({
      label: "Espaces verts min",
      value: `${formatInt(plu.espaces_verts_min_pct)} %`,
    });
  }
  if (plu.division_parcellaire_min_m2 !== null) {
    rows.push({
      label: "Division min",
      value: `${formatInt(plu.division_parcellaire_min_m2)} m²`,
    });
  }

  return (
    <dl className="text-[12.5px]">
      {rows.map((r, i) => (
        <div
          key={r.label}
          className={`flex items-baseline justify-between gap-3 py-1.5 ${
            i > 0 ? "border-t border-ink-100" : ""
          }`}
        >
          <dt className="text-ink-500">{r.label}</dt>
          <dd className="text-ink-900 font-medium tabular-nums text-right">
            {r.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// ─── Reco + caveats ─────────────────────────────────────────────────────

function RecoList({ items }: { items: string[] }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-medium">
        Pistes d'agrandissement
      </div>
      <ul className="mt-2 space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span className="mt-[5px] w-1.5 h-1.5 rounded-full bg-lime-500 shrink-0" />
            <span className="text-[13.5px] text-ink-800 leading-snug">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CaveatsList({ items }: { items: string[] }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-medium">
        Points de vigilance
      </div>
      <ul className="mt-2 space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span className="mt-[5px] w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
            <span className="text-[13.5px] text-ink-800 leading-snug">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Meta + unavailable + error + skeleton ──────────────────────────────

function Meta({
  generatedAt,
  model,
}: {
  generatedAt: string;
  model: string;
}) {
  const date = formatDate(generatedAt);
  return (
    <div className="mt-1 pt-4 border-t border-ink-100 flex items-center gap-2 text-[11px] text-ink-500 leading-snug">
      <Ic.Sparkle size={11} />
      <span>
        Analyse indicative générée automatiquement
        {date ? ` le ${date}` : ""}
        {model ? ` · ${model}` : ""}. Les cotes officielles (CES, reculs,
        destinations) restent celles du règlement PLU applicable — à valider
        en mairie avant tout dépôt.
      </span>
    </div>
  );
}

function UnavailableBox({ data }: { data: AiPotentiel }) {
  return (
    <div className="flex items-start gap-3 rounded-lg bg-ink-50/70 hairline px-4 py-3">
      <div className="w-7 h-7 rounded-full bg-paper hairline grid place-items-center text-ink-600 shrink-0">
        <Ic.Sparkle size={13} />
      </div>
      <div>
        <div className="text-[13.5px] font-medium text-ink-900">
          Potentiel non calculable.
        </div>
        <div className="text-[12.5px] text-ink-600 leading-snug mt-0.5">
          {data.unavailableReason}
        </div>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-4 animate-pulse" aria-hidden>
      <div className="rounded-2xl bg-ink-100/70 h-[108px]" />
      <div className="h-[56px] rounded-xl bg-ink-100/60" />
      <div className="grid md:grid-cols-[200px,1fr] gap-4">
        <div className="h-[180px] rounded-xl bg-ink-100/60" />
        <div className="space-y-2">
          <div className="h-3 rounded bg-ink-100 w-3/4" />
          <div className="h-3 rounded bg-ink-100 w-2/3" />
          <div className="h-3 rounded bg-ink-100 w-3/5" />
          <div className="h-3 rounded bg-ink-100 w-1/2" />
        </div>
      </div>
    </div>
  );
}

function ErrorBox() {
  return (
    <div className="rounded-lg bg-ink-50 px-4 py-3 text-[13px] text-ink-700 leading-relaxed">
      L'analyse de potentiel IA est indisponible pour le moment. Réessayez dans
      quelques minutes.
    </div>
  );
}

// ─── SVG projection ──────────────────────────────────────────────────────

type Ring = Array<[number, number]>;
type SvgView = {
  viewBox: string;
  parcelPath: string;
  buildingPaths: string[];
  buildablePct: number | null;
  /** Total area of the parcel polygon in SVG units (shoelace on the
   *  projected outer ring). Used to size the "buildable hint" green
   *  rectangle proportionally to the actual parcel — earlier versions
   *  used the viewBox area, which over-sized the rectangle on elongated
   *  parcels and caused the visible "spillover beyond the outline". */
  parcelSvgArea: number;
  /** Projected centroid of the parcel — anchor for the buildable hint
   *  so it stays roughly inside the parcel even when the parcel is
   *  off-center within the bounding box. */
  parcelCentroid: { x: number; y: number };
};

function buildSvgView(data: AiPotentiel): SvgView | null {
  const parcel = parseGeom(data.parcelGeometry);
  if (!parcel) return null;

  const buildings: Ring[][] = [];
  for (const b of data.buildings) {
    const parsed = parseGeomBuilding(b);
    if (parsed) buildings.push(parsed);
  }

  // Compute lng/lat bbox of all geometry (parcel + bâtiments).
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  const visit = (rings: Ring[]) => {
    for (const r of rings) {
      for (const [lng, lat] of r) {
        if (lng < minLng) minLng = lng;
        if (lat < minLat) minLat = lat;
        if (lng > maxLng) maxLng = lng;
        if (lat > maxLat) maxLat = lat;
      }
    }
  };
  visit(parcel);
  for (const b of buildings) visit(b);
  if (
    !isFinite(minLng) ||
    !isFinite(maxLng) ||
    maxLng <= minLng ||
    maxLat <= minLat
  ) {
    return null;
  }

  // Equirectangular correction : à 48° N, 1° lng ≈ 73 km contre 1° lat ≈ 111 km.
  // Sans cette correction, la parcelle apparaîtrait écrasée horizontalement.
  const midLat = (minLat + maxLat) / 2;
  const lngScale = Math.cos((midLat * Math.PI) / 180);

  // Plane local en "degrés × cos(lat)" — les distances horizontales et verticales
  // sont maintenant comparables.
  const worldW = (maxLng - minLng) * lngScale;
  const worldH = maxLat - minLat;
  if (worldW <= 0 || worldH <= 0) return null;

  // ViewBox fixe → les stroke widths (1 = 1 unité sur 200) restent lisibles
  // quelle que soit la taille de la parcelle. Sans ça, une parcelle de
  // 2 500 m² donne un viewBox de ~0,0002 unités de large et n'importe quel
  // strokeWidth raisonnable noie tout le SVG.
  const TARGET = 200;
  const PAD = 10; // 5 % de chaque côté
  const inner = TARGET - 2 * PAD;
  const scale = inner / Math.max(worldW, worldH);
  const drawW = worldW * scale;
  const drawH = worldH * scale;
  const offsetX = (TARGET - drawW) / 2;
  const offsetY = (TARGET - drawH) / 2;

  // Projection : lng/lat → SVG units. Y inversé (lat ↑ vs SVG y ↓).
  const project = ([lng, lat]: [number, number]): [number, number] => {
    const px = (lng - minLng) * lngScale * scale;
    const py = (lat - minLat) * scale;
    return [offsetX + px, offsetY + (drawH - py)];
  };

  const projectedParcel = parcel.map((r) => r.map(project));
  const parcelPath = ringsToPath(projectedParcel);
  const buildingPaths = buildings.map((b) =>
    ringsToPath(b.map((r) => r.map(project))),
  );

  // Buildable hint sizing — fraction de la parcelle encore constructible.
  const buildablePct =
    data.residualFootprint_m2 !== null &&
    data.parcelArea_m2 > 0 &&
    data.residualFootprint_m2 > 0
      ? data.residualFootprint_m2 / data.parcelArea_m2
      : null;

  // Outer-ring polygon area (shoelace) and centroid in SVG units.
  // Multipolygons : we use only the outer ring of the first part — the
  // centroid is mostly used as anchor for the buildable hint, and the
  // visual is "good enough" even on multi-part parcels.
  const outerRing = projectedParcel[0] ?? [];
  const parcelSvgArea = computeRingArea(outerRing);
  const parcelCentroid = computeRingCentroid(outerRing) ?? {
    x: TARGET / 2,
    y: TARGET / 2,
  };

  return {
    viewBox: `0 0 ${TARGET} ${TARGET}`,
    parcelPath,
    buildingPaths,
    buildablePct,
    parcelSvgArea,
    parcelCentroid,
  };
}

/** Shoelace formula on a closed (or auto-closed) ring. Returns absolute
 *  area in SVG units². Returns 0 for degenerate rings (< 3 points). */
function computeRingArea(ring: Array<[number, number]>): number {
  if (ring.length < 3) return 0;
  let a = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    a += x1 * y2 - x2 * y1;
  }
  return Math.abs(a) / 2;
}

/** Centroid of a polygon ring (area-weighted, not just the average of
 *  vertices). Returns null on degenerate input. */
function computeRingCentroid(
  ring: Array<[number, number]>,
): { x: number; y: number } | null {
  if (ring.length < 3) return null;
  let cx = 0;
  let cy = 0;
  let twiceArea = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    const cross = x1 * y2 - x2 * y1;
    twiceArea += cross;
    cx += (x1 + x2) * cross;
    cy += (y1 + y2) * cross;
  }
  if (twiceArea === 0) return null;
  return { x: cx / (3 * twiceArea), y: cy / (3 * twiceArea) };
}

function parseGeom(raw: string | null | undefined): Ring[] | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  return toRings(parsed);
}

function parseGeomBuilding(b: AiPotentielBuilding): Ring[] | null {
  return parseGeom(b.geometry);
}

function toRings(g: unknown): Ring[] | null {
  if (!g || typeof g !== "object") return null;
  const geom = g as { type?: string; coordinates?: unknown };
  if (geom.type === "Polygon" && Array.isArray(geom.coordinates)) {
    return (geom.coordinates as unknown[])
      .map(parseRing)
      .filter((r): r is Ring => r !== null);
  }
  if (geom.type === "MultiPolygon" && Array.isArray(geom.coordinates)) {
    const rings: Ring[] = [];
    for (const poly of geom.coordinates as unknown[]) {
      if (!Array.isArray(poly)) continue;
      for (const r of poly) {
        const ring = parseRing(r);
        if (ring) rings.push(ring);
      }
    }
    return rings;
  }
  return null;
}

function parseRing(r: unknown): Ring | null {
  if (!Array.isArray(r)) return null;
  const out: Ring = [];
  for (const pt of r) {
    if (!Array.isArray(pt) || pt.length < 2) continue;
    const x = Number(pt[0]);
    const y = Number(pt[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    out.push([x, y]);
  }
  return out.length >= 3 ? out : null;
}

function ringsToPath(rings: Ring[]): string {
  return rings
    .map((ring) => {
      if (ring.length === 0) return "";
      const [first, ...rest] = ring;
      const head = `M ${first[0]} ${first[1]}`;
      const body = rest.map(([x, y]) => `L ${x} ${y}`).join(" ");
      return `${head} ${body} Z`;
    })
    .filter(Boolean)
    .join(" ");
}

// ─── Formatters ──────────────────────────────────────────────────────────

function formatInt(n: number): string {
  return Math.round(n).toLocaleString("fr-FR");
}
function formatNumber(n: number): string {
  return Number.isInteger(n)
    ? n.toLocaleString("fr-FR")
    : n.toLocaleString("fr-FR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
}
function formatDate(iso: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
